import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  BookOpenCheck,
  Check,
  ChevronRight,
  ExternalLink,
  Files,
  GitCommit,
  History,
  Lock,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  type MetaFunction,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/ui/alert";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { cn } from "@/utils/tailwind";

import { getConceptDefinitionByHref } from "./concepts/registry";
import { CourseFooter } from "./course-footer";
import { CourseShell } from "./course-shell";
import {
  buildBillingHref,
  buildLearningHref,
  buildLearningOriginPath,
  buildPricingHref,
  buildRepositoryCommitHref,
  getLearningOrigin,
  parseRepositoryLineNumbers,
} from "./navigation";
import {
  SourceBrowser,
  SourceFileNavigation,
  SourceFileSelect,
} from "./source-browser";
import type {
  CourseCommitData,
  RepositoryCommitLoaderResponse,
  SourceFilePath,
} from "./types";
import { getCourseSectionLabel } from "./utils";

export const repositoryCommitMeta: MetaFunction = ({ data }) => {
  const loaderData = data as RepositoryCommitLoaderResponse | undefined;
  const commit = loaderData?.commit;

  if (!commit) {
    return [{ title: `Repository | ${publicEnv.siteTitle}` }];
  }

  return [
    { title: `${commit.subject} | ${publicEnv.siteTitle}` },
    {
      name: "description",
      content: `Browse the makefile.fyi repository at commit ${commit.shortCommit}.`,
    },
  ];
};

type RepositoryExplorerTab = "files" | "history";

export function RepositoryCommitPage() {
  const {
    access,
    canAccessPaidContent,
    canAccessSource,
    commit,
    commits,
    dbUser,
    manifest,
    navigation,
    relatedConcepts,
  } = useLoaderData<RepositoryCommitLoaderResponse>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeExplorerTab, setActiveExplorerTab] =
    useState<RepositoryExplorerTab>(() =>
      searchParams.has("file") ? "files" : "history",
    );
  const [isExplorerOpen, setIsExplorerOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const activeFilePath = getActiveFilePath(
    searchParams.get("file"),
    manifest.files.map((sourceFile) => sourceFile.path),
  );
  const requestedLineRanges = searchParams.get("lines");
  const highlightedLineNumbers = useMemo(
    () => parseRepositoryLineNumbers(requestedLineRanges),
    [requestedLineRanges],
  );
  const returnTo = `${location.pathname}${location.search}`;
  const origin = getLearningOrigin(searchParams.get("from"));
  const currentPath = buildLearningOriginPath(
    location.pathname,
    location.search,
  );
  const unlockHref = dbUser
    ? buildBillingHref({ returnTo })
    : buildPricingHref({ returnTo });

  function updateActiveFilePath(filePath: SourceFilePath) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("file", filePath);
    nextSearchParams.delete("lines");
    setSearchParams(nextSearchParams, { replace: true });
  }

  return (
    <CourseShell dbUser={dbUser}>
      <main className="mx-auto flex w-[calc(100%-2rem)] max-w-7xl flex-col gap-8 pb-20 sm:w-[calc(100%-3rem)] sm:gap-10 sm:pb-28">
        <header className="grid gap-6 pt-8 sm:pt-10 lg:pt-12">
          <Link
            className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm transition"
            to={origin?.href ?? "/commits"}
          >
            <ArrowLeft className="size-4" />
            {origin?.label ?? "Repository"}
          </Link>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="grid max-w-4xl gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="font-mono shadow-xs" variant="secondary">
                  <GitCommit />
                  {commit.shortCommit}
                </Badge>
                <Badge className="font-mono shadow-xs" variant="outline">
                  Commit {navigation.position} of {navigation.total}
                </Badge>
                {commit.module && (
                  <Badge className="shadow-xs" variant="outline">
                    {getCourseSectionLabel(commit.module)}
                  </Badge>
                )}
                {access === "paid" && !canAccessSource && (
                  <Badge className="font-mono shadow-xs" variant="outline">
                    <Lock />
                    Full access
                  </Badge>
                )}
              </div>

              <div className="grid gap-3">
                <h1 className="text-3xl leading-tight font-semibold text-balance sm:text-4xl">
                  {commit.subject}
                </h1>
                <p className="text-muted-foreground font-mono text-sm">
                  {commit.date} · {manifest.files.length} files
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
              <Button
                asChild
                className="w-full justify-center sm:w-auto"
                variant="outline"
              >
                <a
                  href={`https://github.com/quietcolin/makefile-fyi/commit/${commit.commit}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  View on GitHub
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>

          <RepositoryCommitNavigation
            next={navigation.next}
            originPath={origin?.href}
            previous={navigation.previous}
          />

          {searchParams.get("purchase") === "success" && (
            <Alert>
              <Check className="size-4" />
              <AlertTitle>Full access unlocked</AlertTitle>
              <AlertDescription>
                This source snapshot and the complete learning library are now
                available on your account.
              </AlertDescription>
            </Alert>
          )}
        </header>

        <section
          aria-labelledby="repository-browser-title"
          className="grid gap-4"
        >
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="grid gap-1">
              <h2
                className="text-xl font-semibold"
                id="repository-browser-title"
              >
                Source snapshot
              </h2>
              <p className="text-muted-foreground text-sm">
                Browse the complete repository exactly as it existed at this
                commit.
              </p>
            </div>
            <Button
              className="hidden gap-1.5 lg:inline-flex"
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsExplorerOpen((isOpen) => !isOpen)}
            >
              {isExplorerOpen ? <PanelLeftClose /> : <PanelLeftOpen />}
              {isExplorerOpen ? "Hide explorer" : "Show explorer"}
            </Button>
          </div>

          <div className="bg-card grid gap-3 rounded-md border p-3 lg:hidden">
            <label
              className="text-muted-foreground text-xs font-medium"
              htmlFor="repository-commit-select"
            >
              Commit
            </label>
            <select
              className="border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 h-9 min-w-0 rounded-md border px-3 font-mono text-xs outline-none focus-visible:ring-[3px]"
              id="repository-commit-select"
              value={commit.shortCommit}
              onChange={(event) => {
                void navigate(
                  buildRepositoryCommitHref({
                    commit: event.target.value,
                    from: origin?.href,
                  }),
                );
              }}
            >
              {commits.map((repositoryCommit, index) => (
                <option
                  key={repositoryCommit.shortCommit}
                  value={repositoryCommit.shortCommit}
                >
                  {index + 1}. {repositoryCommit.shortCommit} —{" "}
                  {repositoryCommit.subject}
                  {repositoryCommit.sourceAccess === "paid" &&
                    !canAccessPaidContent &&
                    " (full access)"}
                </option>
              ))}
            </select>

            <label
              className="text-muted-foreground text-xs font-medium"
              htmlFor="repository-file-select"
            >
              File
            </label>
            <SourceFileSelect
              activeFilePath={activeFilePath}
              id="repository-file-select"
              manifest={manifest}
              onActiveFilePathChange={updateActiveFilePath}
            />
          </div>

          <div
            className={cn(
              "grid min-h-[34rem] gap-3",
              isExplorerOpen &&
                "lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-stretch",
            )}
          >
            {isExplorerOpen && (
              <aside className="bg-card hidden min-h-0 overflow-hidden rounded-md border shadow-sm lg:flex lg:flex-col">
                <div className="grid grid-cols-2 border-b p-1.5">
                  <RepositoryExplorerTabButton
                    icon={History}
                    isActive={activeExplorerTab === "history"}
                    label="History"
                    onClick={() => setActiveExplorerTab("history")}
                  />
                  <RepositoryExplorerTabButton
                    icon={Files}
                    isActive={activeExplorerTab === "files"}
                    label="Files"
                    onClick={() => setActiveExplorerTab("files")}
                  />
                </div>

                {activeExplorerTab === "history" ? (
                  <RepositoryHistory
                    activeCommit={commit.shortCommit}
                    canAccessPaidContent={canAccessPaidContent}
                    commits={commits}
                    originPath={origin?.href}
                  />
                ) : (
                  <SourceFileNavigation
                    activeFilePath={activeFilePath}
                    className="flex flex-1"
                    manifest={manifest}
                    onActiveFilePathChange={updateActiveFilePath}
                  />
                )}
              </aside>
            )}

            {canAccessSource ? (
              <SourceBrowser
                activeFilePath={activeFilePath}
                className="h-[min(48rem,calc(100dvh-10rem))] min-h-[34rem]"
                commit={commit.commit}
                highlightedLineNumbers={highlightedLineNumbers}
                manifest={manifest}
                showFileNavigation={false}
                onActiveFilePathChange={updateActiveFilePath}
              />
            ) : (
              <RepositorySourceLock
                fileCount={manifest.files.length}
                unlockHref={unlockHref}
              />
            )}
          </div>
        </section>

        {(commit.lesson || relatedConcepts.length > 0) && (
          <section
            aria-labelledby="related-learning-title"
            className="grid gap-5"
          >
            <div className="grid gap-1">
              <h2 className="text-xl font-semibold" id="related-learning-title">
                Related learning
              </h2>
              <p className="text-muted-foreground text-sm">
                Move from this raw snapshot to the explanations built around it.
              </p>
            </div>

            <div className="border-border divide-border divide-y border-y">
              {commit.lesson && (
                <Link
                  className="hover:bg-accent/60 group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-2 py-4 transition"
                  to={buildLearningHref({
                    destination: `/course/${commit.shortCommit}`,
                    from: currentPath,
                  })}
                >
                  <BookOpen className="text-muted-foreground size-4" />
                  <span className="grid min-w-0 gap-1">
                    <span className="text-xs font-medium">
                      {commit.lesson.access === "paid" && !canAccessPaidContent
                        ? "Full-access lesson preview"
                        : "Course lesson"}
                    </span>
                    <span className="text-muted-foreground truncate text-sm">
                      {commit.lesson.title}
                    </span>
                  </span>
                  {commit.lesson.access === "paid" && !canAccessPaidContent ? (
                    <Lock className="text-muted-foreground size-4" />
                  ) : (
                    <ChevronRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </Link>
              )}

              {relatedConcepts.map((concept) => {
                const conceptDefinition = getConceptDefinitionByHref(
                  concept.href,
                );
                const isLocked =
                  conceptDefinition?.access === "paid" && !canAccessPaidContent;

                return (
                  <Link
                    key={concept.href}
                    className="hover:bg-accent/60 group grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-2 py-4 transition"
                    to={buildLearningHref({
                      destination: concept.href,
                      from: currentPath,
                    })}
                  >
                    <BookOpenCheck className="text-muted-foreground size-4" />
                    <span className="grid min-w-0 gap-1">
                      <span className="text-xs font-medium">Concept</span>
                      <span className="text-muted-foreground truncate text-sm">
                        {concept.title}
                      </span>
                    </span>
                    {isLocked ? (
                      <Lock className="text-muted-foreground size-4" />
                    ) : (
                      <ChevronRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
                    )}
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>

      <CourseFooter />
    </CourseShell>
  );
}

function RepositoryCommitNavigation({
  next,
  originPath,
  previous,
}: {
  next: CourseCommitData | null;
  originPath: string | undefined;
  previous: CourseCommitData | null;
}) {
  return (
    <nav
      aria-label="Repository commit navigation"
      className="border-border grid divide-y border-y sm:grid-cols-2 sm:divide-y-0"
    >
      {previous ? (
        <Link
          className="hover:bg-accent/60 group grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 px-3 py-4 transition"
          to={buildRepositoryCommitHref({
            commit: previous.commit,
            from: originPath,
          })}
        >
          <ArrowLeft className="text-muted-foreground size-4" />
          <span className="grid min-w-0 gap-1">
            <span className="text-muted-foreground text-xs">
              Previous commit
            </span>
            <span className="truncate text-sm font-medium">
              {previous.subject}
            </span>
          </span>
        </Link>
      ) : (
        <span className="hidden sm:block" />
      )}
      {next && (
        <Link
          className="hover:bg-accent/60 group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-4 text-right transition sm:border-l"
          to={buildRepositoryCommitHref({
            commit: next.commit,
            from: originPath,
          })}
        >
          <span className="grid min-w-0 gap-1">
            <span className="text-muted-foreground text-xs">Next commit</span>
            <span className="truncate text-sm font-medium">{next.subject}</span>
          </span>
          <ArrowRight className="text-muted-foreground size-4" />
        </Link>
      )}
    </nav>
  );
}

function RepositoryHistory({
  activeCommit,
  canAccessPaidContent,
  commits,
  originPath,
}: {
  activeCommit: string;
  canAccessPaidContent: boolean;
  commits: readonly CourseCommitData[];
  originPath: string | undefined;
}) {
  const activeCommitRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    activeCommitRef.current?.scrollIntoView({
      block: "center",
      inline: "nearest",
    });
  }, [activeCommit]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        aria-label="Commit timeline"
        className="min-h-0 flex-1 overflow-y-auto p-2"
      >
        <ol className="grid">
          {commits.map((commit, index) => {
            const isActive = commit.shortCommit === activeCommit;

            return (
              <li key={commit.shortCommit}>
                <Link
                  ref={isActive ? activeCommitRef : undefined}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "group grid grid-cols-[1.75rem_minmax(0,1fr)] gap-2 rounded-sm px-2 py-2 transition",
                    isActive
                      ? "bg-accent text-foreground"
                      : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                  )}
                  to={buildRepositoryCommitHref({
                    commit: commit.commit,
                    from: originPath,
                  })}
                >
                  <span className="relative flex justify-center pt-1.5">
                    {index > 0 && (
                      <span className="border-border absolute -top-2 bottom-1/2 border-l" />
                    )}
                    {index < commits.length - 1 && (
                      <span className="border-border absolute top-1/2 -bottom-2 border-l" />
                    )}
                    <span
                      className={cn(
                        "bg-background ring-border relative z-10 size-2 rounded-full ring-2",
                        isActive && "bg-foreground ring-foreground/20",
                      )}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center justify-between gap-2 font-mono text-[11px]">
                      <span className="inline-flex items-center gap-1">
                        {commit.shortCommit}
                        {commit.sourceAccess === "paid" &&
                          !canAccessPaidContent && <Lock className="size-3" />}
                      </span>
                      <span>
                        {index + 1}/{commits.length}
                      </span>
                    </span>
                    <span className="mt-1 line-clamp-2 text-xs leading-5">
                      {commit.subject}
                    </span>
                    {commit.lesson && (
                      <span className="mt-1.5 inline-flex items-center gap-1 text-[11px]">
                        <BookOpen className="size-3" />
                        Lesson
                        {commit.lesson.access === "paid" && (
                          <Lock className="size-3" />
                        )}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="border-t p-2">
        <Button
          asChild
          className="w-full justify-start"
          size="sm"
          variant="ghost"
        >
          <Link to="/commits">
            <History />
            View full history
          </Link>
        </Button>
      </div>
    </div>
  );
}

function RepositoryExplorerTabButton({
  icon: Icon,
  isActive,
  label,
  onClick,
}: {
  icon: typeof History;
  isActive: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      aria-pressed={isActive}
      className="gap-1.5"
      size="sm"
      type="button"
      variant={isActive ? "secondary" : "ghost"}
      onClick={onClick}
    >
      <Icon />
      {label}
    </Button>
  );
}

function RepositorySourceLock({
  fileCount,
  unlockHref,
}: {
  fileCount: number;
  unlockHref: string;
}) {
  return (
    <div className="bg-card flex min-h-[34rem] items-center justify-center rounded-md border p-6 shadow-sm">
      <div className="grid max-w-md justify-items-center gap-5 text-center">
        <span className="bg-secondary text-secondary-foreground ring-border/70 flex size-12 items-center justify-center rounded-md shadow-xs ring-1">
          <Lock className="size-5" />
        </span>
        <div className="grid gap-2">
          <h3 className="text-xl font-semibold">Unlock this source snapshot</h3>
          <p className="text-muted-foreground leading-7">
            Commit metadata remains public. Full access unlocks all {fileCount}
            files, the related lesson, and every paid concept and replay.
          </p>
        </div>
        <Button asChild>
          <Link to={unlockHref}>View full-access options</Link>
        </Button>
      </div>
    </div>
  );
}

function getActiveFilePath(
  requestedFilePath: string | null,
  filePaths: readonly SourceFilePath[],
) {
  if (requestedFilePath && filePaths.includes(requestedFilePath)) {
    return requestedFilePath;
  }

  return (
    filePaths.find((filePath) => filePath === "Makefile") ?? filePaths[0] ?? ""
  );
}
